import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Table, { Badge, TableLink } from '../components/ui/Table';
import Modal from '../components/ui/Modal';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface CardOfWeek {
  id: string;
  type: string;
  cardName: string;
  cardImage: string | null;
  message: string;
  sendDate: string;
  state: string;
}

export default function CartaSemana() {
  const [cards, setCards] = useState<CardOfWeek[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<CardOfWeek | null>(null);
  const [formData, setFormData] = useState({
    type: 'week',
    cardName: '',
    cardImage: '',
    message: '',
    sendDate: '',
    state: 'scheduled'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAdminAuth('/api/admin/card-of-week');
      if (response.ok) {
        const data = await response.json();
        setCards(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
      setToast({ message: 'Erro ao carregar campanhas', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditingCard(null);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(21, 0, 0, 0);
    setFormData({
      type: 'week',
      cardName: '',
      cardImage: '',
      message: '',
      sendDate: tomorrow.toISOString().slice(0, 16),
      state: 'scheduled'
    });
    setIsModalOpen(true);
  };

  const handleEdit = (card: CardOfWeek) => {
    setEditingCard(card);
    setFormData({
      type: card.type,
      cardName: card.cardName,
      cardImage: card.cardImage || '',
      message: card.message,
      sendDate: new Date(card.sendDate).toISOString().slice(0, 16),
      state: card.state
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const url = editingCard 
        ? `/api/admin/card-of-week/${editingCard.id}`
        : '/api/admin/card-of-week';
      const method = editingCard ? 'PUT' : 'POST';
      
      const response = await fetchWithAdminAuth(url, {
        method,
        body: JSON.stringify({
          ...formData,
          sendDate: new Date(formData.sendDate).toISOString()
        })
      });
      
      if (response.ok) {
        setToast({ message: 'Campanha guardada com sucesso!', type: 'success' });
        setIsModalOpen(false);
        fetchCards();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      setToast({ message: 'Erro ao guardar campanha', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja eliminar esta campanha?')) return;
    
    try {
      const response = await fetchWithAdminAuth(`/api/admin/card-of-week/${id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setToast({ message: 'Campanha eliminada com sucesso!', type: 'success' });
        fetchCards();
      }
    } catch (error) {
      setToast({ message: 'Erro ao eliminar campanha', type: 'error' });
    }
  };

  const handleMarkAsSent = async (id: string) => {
    try {
      const response = await fetchWithAdminAuth(`/api/admin/card-of-week/${id}/send`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        setToast({ message: 'Campanha marcada como enviada!', type: 'success' });
        fetchCards();
      }
    } catch (error) {
      setToast({ message: 'Erro ao atualizar campanha', type: 'error' });
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    return `${dayNames[date.getDay()]}, ${date.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getStateLabel = (state: string) => {
    switch (state) {
      case 'scheduled': return 'A decorrer';
      case 'sent': return 'Enviada';
      case 'cancelled': return 'Cancelada';
      default: return state;
    }
  };

  const getStateVariant = (state: string): 'success' | 'warning' | 'danger' => {
    switch (state) {
      case 'scheduled': return 'success';
      case 'sent': return 'warning';
      case 'cancelled': return 'danger';
      default: return 'warning';
    }
  };

  const columns = [
    { header: 'Tipo', key: 'type', render: (val: string) => val === 'week' ? 'Semana' : 'Fim de semana' },
    { header: 'Carta', key: 'cardName' },
    { header: 'Envio', key: 'sendDate', render: (val: string) => formatDate(val) },
    { 
      header: 'Estado', 
      key: 'state', 
      render: (val: string) => <Badge variant={getStateVariant(val)}>{getStateLabel(val)}</Badge>
    },
    { 
      header: '', 
      key: 'actions', 
      render: (_val: any, row: CardOfWeek) => (
        <div className="flex gap-2">
          <TableLink onClick={() => handleEdit(row)}>Editar</TableLink>
          {row.state === 'scheduled' && (
            <TableLink onClick={() => handleMarkAsSent(row.id)}>Marcar enviada</TableLink>
          )}
          <TableLink onClick={() => handleDelete(row.id)}>Eliminar</TableLink>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card title="Carta da Semana" subtitle="A carregar...">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Carta da Semana / Fim de semana"
        subtitle="Defina a carta, o texto e a data de envio para site e ManyChat."
        action={<Button variant="primary" onClick={handleNew}>+ Nova campanha</Button>}
      >
        <Table columns={columns} data={cards} />
        {cards.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhuma campanha encontrada. Clique em "+ Nova campanha" para criar.
          </div>
        )}
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingCard ? 'Editar Campanha' : 'Nova Campanha'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Tipo</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            >
              <option value="week">Semana</option>
              <option value="weekend">Fim de semana</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Nome da Carta</label>
            <input
              type="text"
              value={formData.cardName}
              onChange={(e) => setFormData({ ...formData, cardName: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="ex: O Carro"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">URL da Imagem</label>
            <input
              type="text"
              value={formData.cardImage}
              onChange={(e) => setFormData({ ...formData, cardImage: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Mensagem</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white min-h-[120px]"
              placeholder="Mensagem da carta da semana..."
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Data de Envio</label>
            <input
              type="datetime-local"
              value={formData.sendDate}
              onChange={(e) => setFormData({ ...formData, sendDate: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1">Estado</label>
            <select
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            >
              <option value="scheduled">A decorrer</option>
              <option value="sent">Enviada</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'A guardar...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
